import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { InvitationTimelineResponse } from 'core/types/invitation.interface';
import { IUser } from 'shared/types/user.interface';
import { User, UserTypes } from 'shared/types/user.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UsersService } from 'core';
import { InvitationsService } from 'core/services/invitations.service';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { MyForestUserInfoDefaultComponent } from '../my-forest-user-info-default/my-forest-user-info-default.component';
import { MyForestUserInfoSpecialComponent } from '../my-forest-user-info-special/my-forest-user-info-special.component';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../blocks/breadcrumb/breadcrumb.service';
import { BalanceService } from 'core/services/balance.service';
import { AuthService } from 'core/services/auth.service';
import { IUserSharedGanas } from 'core/types/balance-transaction.interface';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-my-forest-user-info',
  imports: [
    KorviuLoaderComponent,
    MyForestUserInfoDefaultComponent,
    MyForestUserInfoSpecialComponent,
    BreadcrumbComponent,
    TranslatePipe
  ],
  templateUrl: './my-forest-user-info.component.html',
  styleUrl: './my-forest-user-info.component.scss'
})
export class MyForestUserInfoComponent implements OnInit, OnDestroy {
  #route = inject(ActivatedRoute);
  #usersService = inject(UsersService);
  #destroyRef = inject(DestroyRef);
  #invitationsService = inject(InvitationsService);
  #breadcrumbService = inject(BreadcrumbService);
  #balanceService = inject(BalanceService);
  #authService = inject(AuthService);
  #translateService = inject(TranslateService);

  isGanacocio = computed<boolean>(() => this.user()?.userType === UserTypes.PR);
  isIndirectRegularMember = computed<boolean>(() => {
    const user = this.user();
    return !!user && !user.isSpecial && !user.isDirectReferral;
  });

  user = signal<User>(null);
  userTimeline = signal<InvitationTimelineResponse>(null);
  userIsLoading = signal(false);
  userNotFound = signal(false);

  ganasShared = signal(0);
  ganamigoGanasShared = signal(0);

  ngOnInit(): void {
    this.#translateService.onLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#updateBreadcrumb());
    const userPublicId: string = this.#route.snapshot.params['userPublicId'];
    this.#loadUser(userPublicId);
  }

  ngOnDestroy(): void {
    this.#breadcrumbService.setCurrentPageBreadcrumb('', 'replacer=username');
  }

  #loadUser(userPublicId: string): void {
    this.#breadcrumbService.setCurrentPageBreadcrumb('', 'replacer=username');
    this.userIsLoading.set(true);
    this.userNotFound.set(false);
    this.#usersService
      .getMyForestUserInfoByIdentifier(userPublicId)
      .pipe(
        switchMap((user: IUser) =>
          this.#usersService.getMyForestUserByIdentifier(user.public_id ?? userPublicId).pipe(
            catchError(() => of(null)),
            map((forestUser: IUser | null) => ({
              ...user,
              // user_id selects a forest's descendants, so its first row may be a different member.
              ...(forestUser?.id === user.id && forestUser?.is_deleted
                ? { is_deleted: true, full_name: forestUser.full_name }
                : {}),
              referred_by: this.#resolveReferredBy(user, forestUser),
              is_direct_referral: this.#resolveIsDirectReferral(user, forestUser)
            }))
          )
        ),
        switchMap((user: IUser) =>
          this.#balanceService.getSharedGanasInfo(user.id).pipe(
            catchError(() => of(null)),
            map((response: IUserSharedGanas | null) => {
              this.ganasShared.set(response?.shared?.amount ?? 0);
              this.ganamigoGanasShared.set(response?.received?.amount ?? 0);
              // PR #320 exposes is_deleted here before the ordinary user-detail serializer supports it.
              return response?.is_deleted
                ? { ...user, is_deleted: true, full_name: response.name || user.full_name }
                : user;
            })
          )
        ),
        switchMap((user: IUser) => {
          const userObj = new User(user);
          this.user.set(userObj);
          this.#updateBreadcrumb();
          return this.#getTimeline(userObj).pipe(catchError(() => of(null)));
        }),
        tap((timeline: InvitationTimelineResponse) => this.userTimeline.set(timeline)),
        catchError((error: unknown) => {
          this.userNotFound.set(this.#isNotFoundError(error));
          this.user.set(null);
          this.userTimeline.set(null);
          this.ganasShared.set(0);
          this.ganamigoGanasShared.set(0);
          return of(null);
        }),
        finalize(() => {
          this.userIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #updateBreadcrumb(): void {
    const user = this.user();
    const name = user?.isDeleted
      ? this.#translateService.instant('common.deletedAccount', { id: user.id })
      : user?.fullName || '';
    this.#breadcrumbService.setCurrentPageBreadcrumb(name, 'replacer=username');
  }

  #getTimeline(user: User): Observable<InvitationTimelineResponse> {
    return this.#invitationsService.getUserTimeline(user.id);
  }

  #resolveReferredBy(user: IUser, forestUser: IUser | null): IUser['referred_by'] {
    const candidates = [user.referred_by, forestUser?.referred_by, user.user_parent?.full_name];
    const referredBy = candidates.find(candidate => this.#isValidReferredBy(candidate, user));

    if (referredBy) {
      return referredBy;
    }

    const currentUser = this.#authService.currentUser;
    const isDirectReferral = forestUser?.is_direct_referral ?? user.is_direct_referral;

    if (isDirectReferral && currentUser?.id !== user.id) {
      return currentUser?.fullName || null;
    }

    return null;
  }

  #resolveIsDirectReferral(user: IUser, forestUser: IUser | null): boolean {
    const currentUser = this.#authService.currentUser;
    const referredBy = user.referred_by;

    if (currentUser && referredBy) {
      if (typeof referredBy !== 'string') {
        if (typeof referredBy.is_self === 'boolean') {
          return referredBy.is_self;
        }

        if (referredBy.id != null && currentUser.id != null) {
          return referredBy.id === currentUser.id;
        }
      } else if (currentUser.fullName) {
        return this.#normalizeName(referredBy) === this.#normalizeName(currentUser.fullName);
      }
    }

    return !!(forestUser?.is_direct_referral ?? user.is_direct_referral);
  }

  #isValidReferredBy(candidate: IUser['referred_by'], user: IUser): boolean {
    const fullName = typeof candidate === 'string' ? candidate : candidate?.full_name;

    if (!fullName) {
      return false;
    }

    if (typeof candidate !== 'string' && candidate?.id && candidate.id === user.id) {
      return false;
    }

    return (
      this.#normalizeName(fullName) !== this.#normalizeName(user.full_name || `${user.first_name} ${user.last_name}`)
    );
  }

  #normalizeName(name: string): string {
    return name.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  #isNotFoundError(error: unknown): boolean {
    const httpError = error as { code?: unknown; status?: unknown };
    return httpError.code === 404 || httpError.status === 404;
  }
}
